import { useFormik } from 'formik';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import {
  authorizedRepAttestation,
  beneficialOwnerAttestation,
  consentToCreditReportAttestation,
  controlPersonAttestation,
} from './complianceAttestations';
import { useGetFeatureFlagsQuery } from '../../../../api/features';
import {
  personalInfoLargeBusinessInfoValidationSchema,
  personalInfoSmallBusinessValidationSchema,
} from '../../../../config/page-validation-schema';
import { CREDIT_REPORT_AUTH_DOC } from '../../../../domain/config';
import { PersonalInfo, ssnFormat } from '../../../../domain/person';
import { isSmallBusiness } from '../../../../domain/qualification';
import { SuccessChip } from '../../../../general/components/success-chip';
import { FormikDateField } from '../../../../general/inputs/formik-date-field';
import { FormikPasswordField } from '../../../../general/inputs/formik-password-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { RootState } from '../../../../store';
import { AddressFormFields } from '../../../address/components/address-form-fields';
import { AddressFormFieldsWithAutocomplete } from '../../../address/components/address-form-fields-with-autocomplete';
import { recordConsent } from '../../../legal-consent/reducers/consent';
import { creditReportAuthorizationSelector } from '../../../legal-consent/selectors/legal-document-selectors';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { savePersonalInfo, savePersonalInfoLargeBusiness } from '../../reducers/personalInfo';

const selector = ({
  personalInfo: { info: personalInfo },
  nextStep: { loading: submitting },
  addressLookup: { loading: verifyingAddress },

  businessInfo: { info: businessInfo },
  consent: { loading: savingConsent },
}: RootState) => ({
  working: submitting || verifyingAddress || savingConsent,
  personalInfo,
  businessInfo,
});

const PersonalInfoForm: FC = () => {
  const { data: featureFlags } = useGetFeatureFlagsQuery();
  const dispatch = useDispatch();
  const { working, personalInfo, businessInfo } = useSelector(selector);
  const creditReportAgreement = useSelector(creditReportAuthorizationSelector);
  const consentToCreditReportLabel = consentToCreditReportAttestation(creditReportAgreement);
  const smallBusiness = isSmallBusiness(businessInfo);
  const personalInfoSchema = smallBusiness
    ? personalInfoSmallBusinessValidationSchema
    : personalInfoLargeBusinessInfoValidationSchema;

  const consentSchema = smallBusiness
    ? {
        creditReportConsent: yup
          .boolean()
          .required()
          .oneOf([true], 'Consent to Consumer Credit Report is required to proceed'),
      }
    : undefined;

  const formik = useFormik<{ personalInfo: PersonalInfo; creditReportConsent: boolean }>({
    initialValues: {
      personalInfo: {
        ...personalInfo,
        ssnLast4: personalInfo.ssnLast4 || '',
      },
      creditReportConsent: false,
    },
    validationSchema: yup.object({
      personalInfo: personalInfoSchema,
      ...consentSchema,
    }),
    onSubmit: (values: { personalInfo: Partial<PersonalInfo> }) => {
      if (smallBusiness) {
        dispatch(
          recordConsent({
            documentKeys: [CREDIT_REPORT_AUTH_DOC],
            onSuccess: [savePersonalInfo(values.personalInfo as PersonalInfo)],
          }),
        );
      } else {
        dispatch(savePersonalInfoLargeBusiness(values.personalInfo as PersonalInfo));
      }
    },
  });

  if (!smallBusiness) {
    return (
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField formik={formik} id="personalInfo.firstName" label="First Name" name="personalInfo.firstName" />
        <FormikTextField formik={formik} id="personalInfo.lastName" label="Last Name" name="personalInfo.lastName" />
        <FormikToggle
          name="personalInfo.authorizedRep"
          formik={formik}
          id="personalInfo.authorizedRep"
          label={authorizedRepAttestation}
          toggleStyle="checkbox"
        />
        <SubmitWorkflowButton type="submit" progress={working} sx={{ mt: 3 }}>
          Next
        </SubmitWorkflowButton>
      </form>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikTextField formik={formik} id="personalInfo.firstName" label="First Name" name="personalInfo.firstName" />
      <FormikTextField formik={formik} id="personalInfo.lastName" label="Last Name" name="personalInfo.lastName" />
      <FormikDateField formik={formik} id="personalInfo.dob" label="Date of birth" name="personalInfo.dob" />
      {!featureFlags?.qfEnableAddressAutocomplete && <AddressFormFields objectPrefix="personalInfo" formik={formik} />}
      {featureFlags?.qfEnableAddressAutocomplete && (
        <AddressFormFieldsWithAutocomplete objectPrefix="personalInfo" formik={formik} />
      )}
      <FormikPasswordField
        formik={formik}
        id="personalInfo.ssnLast4"
        label="Last 4 Digits of SSN"
        name="personalInfo.ssnLast4"
        format={ssnFormat}
        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
        helperText="We use the last 4 digits of your Social Security Number to verify your identity and provide you with the best offer."
      />
      <FormikToggle
        name="personalInfo.beneficialOwner"
        formik={formik}
        id="personalInfo.beneficialOwner"
        label={beneficialOwnerAttestation}
        toggleStyle="checkbox"
      />
      <FormikToggle
        name="personalInfo.controlPerson"
        formik={formik}
        id="personalInfo.controlPerson"
        label={controlPersonAttestation}
        toggleStyle="checkbox"
      />
      <FormikToggle
        name="personalInfo.authorizedRep"
        formik={formik}
        id="personalInfo.authorizedRep"
        label={authorizedRepAttestation}
        toggleStyle="checkbox"
      />
      <FormikToggle
        name="creditReportConsent"
        formik={formik}
        id="creditReportConsent"
        label={consentToCreditReportLabel}
        toggleStyle="checkbox"
      />
      <SuccessChip
        size="small"
        label="Applying won't affect your personal credit score!"
        sx={{
          display: 'flex',
          mx: 0,
          mt: 2,
          mb: 1,
          p: 0,
        }}
      />
      <SubmitWorkflowButton type="submit" progress={working} sx={{ mt: 3 }}>
        Next
      </SubmitWorkflowButton>
    </form>
  );
};

export { PersonalInfoForm };
